import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, X, PackageCheck, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * FinalPOFanoutModal — Phase 5, `final_po_fanout` stage.
 *
 * This is NOT an approve/hold/reject decision — it is a fulfilment log for the
 * twin process owners (Amey.G accounts side + Shivmohan.V transport side).
 * Once BOTH have marked fulfilled, the requisition auto-closes to completed.
 *
 * Rules:
 *   • Remarks OPTIONAL (fulfilment log, not a decision).
 *   • Single "Mark Fulfilled" button.
 *   • If the OTHER party has already marked but this user hasn't → show a
 *     "waiting for you — the other side is done" banner.
 *   • If THIS user has already marked but the other side hasn't → the parent
 *     shows the "waiting for <other>" banner and this modal is not invoked.
 */
export default function FinalPOFanoutModal({ open, onClose, onDone, requisition, currentUser }) {
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setRemarks(""); setError("");
  }, [open, requisition?.ref_code]);

  if (!open || !requisition) return null;

  const trimmed = remarks.trim();

  // Determine which side this user represents so we can show the right banner.
  const isAccountsSide = currentUser?.id === requisition.accounts_id;
  const isTransportSide = currentUser?.id === requisition.transport_po_id;
  const otherSideDone = isAccountsSide
    ? !!requisition.fulfilled_by_transport_at
    : isTransportSide
      ? !!requisition.fulfilled_by_accounts_at
      : false;
  const otherSideName = isAccountsSide
    ? (requisition.transport_po_name || "Transport PO")
    : (requisition.accounts_name || "Accounts PO");

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(
        `/requisitions/${requisition.ref_code}/action`,
        { action: "mark_fulfilled", remarks: trimmed },
      );
      const closed = data?.status === "completed";
      toast.success(closed
        ? `Fulfilled — requisition ${requisition.ref_code} is now Completed.`
        : `Marked fulfilled — waiting for ${otherSideName} to also mark.`);
      onDone?.(data);
      onClose?.();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="final-po-fanout-modal"
    >
      <div
        className="w-full max-w-lg bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <PackageCheck className="text-[#2d6b6b]" size={18} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              Mark Fulfilled — {requisition.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="text-xs text-[#4a524c] bg-[#faf5eb] border-l-4 border-[#2d6b6b] px-3 py-2 rounded">
            Both <strong>Accounts</strong> and <strong>Transport</strong> must mark this
            requisition as fulfilled before it moves to <strong>Completed</strong>.
          </div>

          {otherSideDone && (
            <div className="text-xs text-white bg-[#2d6b6b] px-3 py-2 rounded flex items-start gap-2"
                 data-testid="other-side-done-banner">
              <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
              <div>
                <strong>{otherSideName} has already marked fulfilled.</strong> Your
                mark will close this requisition.
              </div>
            </div>
          )}

          {!otherSideDone && (
            <div className="text-xs text-[#4a524c] bg-white border border-[#e2d7c3] px-3 py-2 rounded flex items-start gap-2"
                 data-testid="waiting-other-banner">
              <Clock size={14} className="mt-0.5 shrink-0 text-[#b48a45]" />
              <div>
                Waiting for <strong>{otherSideName}</strong> to also mark fulfilled.
                Your mark alone will not close the requisition.
              </div>
            </div>
          )}

          {error && (
            <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded" data-testid="modal-server-error">
              {error}
            </div>
          )}

          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Remarks
              <span className="ml-2 text-[#6e7770] text-xs font-normal">(optional)</span>
            </span>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value.slice(0, 1000))}
              rows={3}
              placeholder="Optional note for the fulfilment log…"
              className="input-vgws min-h-[80px]"
              data-testid="final-po-remarks"
            />
          </label>
        </div>

        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost" data-testid="final-po-cancel">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white border shadow-sm bg-[#2d6b6b] hover:bg-[#1c4a4a] border-[#1c4a4a] ${busy ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="final-po-confirm"
          >
            {busy
              ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <CheckCircle2 size={16} />}
            {busy ? "Marking…" : "Mark Fulfilled"}
          </button>
        </div>
      </div>
    </div>
  );
}
